import { h } from "preact";
import { useState, useLayoutEffect, useRef } from "preact/hooks";
import ProductOptionSwatch from "./product-option-swatch";

// Number of rows shown before truncation. The show-more button occupies the
// last slot of the final visible row.
const VISIBLE_ROWS = 2;

// Measures how many swatches fit on a single row so the cap follows the actual
// container width rather than a breakpoint. Returns null until measured, in
// which case every swatch renders.
function useSwatchesPerRow(listRef, enabled) {
  const [perRow, setPerRow] = useState(null);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!enabled || !list) return;

    const measure = () => {
      const item = list.querySelector(".product-options__swatch-wrapper");
      if (!item) return;

      const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
      const itemWidth = item.getBoundingClientRect().width;
      const listWidth = list.getBoundingClientRect().width;
      if (!itemWidth || !listWidth) return;

      setPerRow(Math.max(1, Math.floor((listWidth + gap) / (itemWidth + gap))));
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(list);
    return () => observer.disconnect();
  }, [enabled]);

  return perRow;
}

export default function ProductOptionSwatchList({
  legend,
  optionValues,
  optionName,
  updateOptionValue,
  selectedOptionValue,
  disabledOptionValues = [],
  truncate = false,
}) {
  const [expanded, setExpanded] = useState(false);
  const listRef = useRef(null);
  const perRow = useSwatchesPerRow(listRef, truncate && !expanded);

  const total = optionValues.length;
  const max = perRow ? perRow * VISIBLE_ROWS : null;
  const isTruncated = truncate && !expanded && max !== null && total > max;
  // Reserve one slot for the show-more button.
  const visibleCount = isTruncated ? max - 1 : total;
  const visibleValues = optionValues.slice(0, visibleCount);
  const additionalCount = total - visibleCount;

  return (
    <fieldset className="product-options__swatch-list" ref={listRef}>
      <legend className="visually-hidden">{legend}</legend>
      {visibleValues.map((optionValue) => (
        <ProductOptionSwatch
          key={optionValue.name}
          optionValue={optionValue}
          optionName={optionName}
          disabled={disabledOptionValues.includes(optionValue.name)}
          updateOptionValue={updateOptionValue}
          selected={optionValue.name === selectedOptionValue}
        />
      ))}
      {isTruncated && (
        <button
          type="button"
          className="product-options__swatch-more"
          onClick={() => setExpanded(true)}
        >
          +{additionalCount}
        </button>
      )}
    </fieldset>
  );
}
